import React from "react";
import { ShieldAlert } from "lucide-react";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Button } from "./UI";
import { Link } from "react-router-dom";

/**
 * Route/section guard. Renders children only if the caller holds `require`
 * (a single permission key) or any of `requireAny` (array). Otherwise shows
 * a premium "Access restricted" state — no widgets, no data, no leak.
 */
export default function PermissionGate({ require, requireAny, children, fallback }) {
  const { loading, has, hasAny, permissions } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-2xl bg-primary/20" />
          <div className="text-muted-foreground text-sm">Verifying permissions…</div>
        </div>
      </div>
    );
  }

  const allowed =
    (!require && !requireAny) ||
    (require && has(require)) ||
    (requireAny && hasAny(requireAny));

  if (allowed) return children;
  if (fallback) return fallback;

  return (
    <div className="p-1 animate-fade-up">
      <PageHero
        eyebrow="Access restricted"
        title="This module is not part of your role"
        subtitle="Your Home360 permissions do not include this area. If you believe this is a mistake, contact your Home360 Super Administrator."
        actions={<Link to="/"><Button variant="primary" data-testid="access-denied-home-btn">Go to my dashboard</Button></Link>}
      >
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldAlert className="w-4 h-4" /> Permission required: <code className="font-mono">{require || (requireAny || []).join(" | ")}</code>
        </div>
      </PageHero>
    </div>
  );
}
